// src/scenes/incomes/Incomes.jsx

import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Modal,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { lighten } from "@mui/material/styles";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import IncomeEntry from "../../component-files/Forms/MGRIncomeEntry";
import { useAuth } from "../../context/AuthContext";

const BASE = "https://figazuresql.swancreate.com/api/swa";
const API_INCOMES = `${BASE}/FINANCE_Incomes`;
const API_CATEGORIES = `${BASE}/finance_categories`;
const API_SUBCATEGORIES = `${BASE}/finance_subcategories`;
const API_SCHOOLS = `${BASE}/SchoolMst`;
const API_REIMBURSE_PROGS = `${BASE}/FINANCE_ReimbursementPrograms`;

export default function Incomes() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { allocations } = useAuth();
    const allowedIds = allocations.map(a => a.SchoolId);

    const [incomes, setIncomes] = useState([]);
    const [cats, setCats] = useState([]);
    const [subcats, setSubcats] = useState([]);
    const [schools, setSchools] = useState([]);
    const [progs, setProgs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [monthFilter, setMonthFilter] = useState("");
    const [siteFilter, setSiteFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [entryOpen, setEntryOpen] = useState(false);

    // Re-run lookups & data load whenever allocations change
    useEffect(() => {
        loadLookups();
        loadIncomes();
    }, [allocations]);

    async function loadLookups() {
        try {
            const [c, sc, s, p] = await Promise.all([
                fetch(API_CATEGORIES).then(r => r.json()),
                fetch(API_SUBCATEGORIES).then(r => r.json()),
                fetch(API_SCHOOLS).then(r => r.json()),
                fetch(API_REIMBURSE_PROGS).then(r => r.json()),
            ]);
            s.sort((a, b) => a.SchoolName.localeCompare(b.SchoolName));
            setCats(c.filter(x => x.Type === "Income"));
            setSubcats(sc);
            // only show the schools allocated to this user
            setSchools(s.filter(sch => allowedIds.includes(sch.SchoolId)));
            setProgs(p);
        } catch (e) {
            console.error("Lookup load failed", e);
            setError("Lookup load failed");
        }
    }

    async function loadIncomes() {
        setLoading(true);
        try {
            const data = await fetch(API_INCOMES).then(r => r.json());
            // restrict to allocated
            setIncomes(data.filter(r => allowedIds.includes(r.SchoolId)));
        } catch (e) {
            console.error("Income load failed", e);
            setError("Income load failed");
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete(id) {
        try {
            await fetch(`${API_INCOMES}/${id}`, { method: "DELETE" });
            setIncomes(ins => ins.filter(r => r.apiid !== id));
        } catch (e) {
            console.error("Delete failed", e);
            setError("Delete failed");
        }
    }

    const handleCellEditCommit = async params => {
        const { id: rowApiid, field, value } = params;
        let body = {};
        if (field === "SiteName") {
            const school = schools.find(s => s.SchoolName === value);
            if (!school) return console.error("Unknown school", value);
            body.SchoolId = school.SchoolId;
            body.SiteName = value;
        } else {
            body[field] = value;
        }
        try {
            const res = await fetch(`${API_INCOMES}/${rowApiid}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body)
            });
            if (res.ok) {
                setIncomes(prev =>
                    prev.map(r => (r.apiid === rowApiid ? { ...r, ...body } : r))
                );
            } else {
                console.error("Update failed", res.status, await res.text());
            }
        } catch (e) {
            console.error("Error updating", e);
        }
    };

    const months = Array.from(
        new Set(
            incomes
                .filter(r => r.IncomeDate)
                .map(r =>
                    new Date(r.IncomeDate).toLocaleString("en-US", { month: "long" })
                )
        )
    );

    const filtered = incomes.filter(r => {
        const okM =
            !monthFilter ||
            (r.IncomeDate &&
                new Date(r.IncomeDate).toLocaleString("en-US", { month: "long" }) ===
                monthFilter);
        const okS = !siteFilter || r.SiteName === siteFilter;
        const okC = !categoryFilter || r.IncomeCategoryID === categoryFilter;
        return okM && okS && okC;
    });

    const mainCols = [
        {
            field: "SiteName",
            headerName: "Site",
            flex: 1,
            editable: true,
            type: "singleSelect",
            valueOptions: schools.map(s => s.SchoolName),
        },
        {
            field: "IncomeCategory",
            headerName: "Category",
            flex: 1,
            type: "singleSelect",
            valueOptions: cats.map(c => c.CategoryName),
        },
        {
            field: "IncomeSubcategory",
            headerName: "Subcategory",
            flex: 1,
            type: "singleSelect",
            valueOptions: params => {
                const cat = cats.find(c => c.CategoryName === params.row.IncomeCategory);
                return subcats
                    .filter(sc => sc.CategoryID === cat?.CategoryID)
                    .map(sc => sc.SubcategoryName);
            },
        },
        {
            field: "Amount",
            headerName: "Amount",
            flex: 1,
            valueFormatter: ({ value }) =>
                new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD"
                }).format(value || 0),
        },
        {
            field: "IncomeDate",
            headerName: "Date",
            flex: 1,
            renderCell: ({ row }) =>
                row.IncomeDate
                    ? new Date(row.IncomeDate).toLocaleDateString("en-US")
                    : "N/A",
        },
        { field: "Description", headerName: "Description", flex: 1 },
        {
            field: "ReimbursementTrackingProgram",
            headerName: "Reimb Prog",
            flex: 1,
            type: "singleSelect",
            valueOptions: ["", ...progs.map(p => p.program_abbreviation).filter(Boolean)],
        },
        {
            field: "actions",
            headerName: "Actions",
            flex: 0.5,
            sortable: false,
            filterable: false,
            renderCell: ({ row }) => (
                <Tooltip title="Delete">
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(row.apiid)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    return (
        <Box
            m="20px"
            sx={{
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                minHeight: "100vh",
                p: 2,
                borderRadius: 2,
                boxShadow: `0 0 30px 5px ${theme.palette.divider}`
            }}
        >
            <Header title="INCOMES" subtitle="Financial Management" />

            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                <FormControl sx={{ minWidth: 160 }}>
                    <InputLabel>Month</InputLabel>
                    <Select
                        value={monthFilter}
                        onChange={e => setMonthFilter(e.target.value)}
                        label="Month"
                    >
                        <MenuItem value="">All</MenuItem>
                        {months.map(m => (
                            <MenuItem key={m} value={m}>
                                {m}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 160 }}>
                    <InputLabel>Site</InputLabel>
                    <Select
                        value={siteFilter}
                        onChange={e => setSiteFilter(e.target.value)}
                        label="Site"
                    >
                        <MenuItem value="">All</MenuItem>
                        {schools.map(s => (
                            <MenuItem key={s.SchoolId} value={s.SchoolName}>
                                {s.SchoolName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 160 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                        value={categoryFilter}
                        onChange={e => setCategoryFilter(e.target.value)}
                        label="Category"
                    >
                        <MenuItem value="">All</MenuItem>
                        {cats.map(c => (
                            <MenuItem key={c.CategoryID} value={c.CategoryID}>
                                {c.CategoryName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Box flexGrow={1} />

                <Button
                    variant="contained"
                    color="success"
                    onClick={() => setEntryOpen(true)}
                >
                    Add Income
                </Button>
            </Box>

            {loading ? (
                <CircularProgress />
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <Box
                    height="75vh"
                    sx={{
                        "& .MuiDataGrid-root": { border: "none" },
                        "& .MuiDataGrid-row:nth-of-type(even)": {
                            backgroundColor:
                                theme.palette.mode === "light"
                                    ? theme.palette.action.hover
                                    : colors.primary[600]
                        },
                        "& .MuiDataGrid-row:nth-of-type(odd)": {
                            backgroundColor:
                                theme.palette.mode === "light"
                                    ? theme.palette.background.paper
                                    : lighten(colors.primary[600], 0.3)
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: colors.primary[700],
                            color: theme.palette.text.primary
                        },
                        "& .MuiDataGrid-footerContainer": {
                            backgroundColor: colors.primary[700],
                            color: theme.palette.text.primary
                        },
                        "& .MuiDataGrid-cell": {
                            color: theme.palette.text.primary
                        }
                    }}
                >
                    <DataGrid
                        rows={filtered}
                        columns={mainCols}
                        getRowId={r => r.apiid}
                        pageSize={50}
                        rowsPerPageOptions={[10, 20, 50]}
                        disableSelectionOnClick
                        density="compact"
                        editable
                        onCellEditCommit={handleCellEditCommit}
                    />
                </Box>
            )}

            <Modal
                open={entryOpen}
                onClose={() => setEntryOpen(false)}
                sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
            >
                <Box
                    sx={{
                        position: "relative",
                        width: "80%",
                        height: "80%",
                        bgcolor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        p: 2,
                        borderRadius: 2,
                        boxShadow: 24,
                        overflow: "auto"
                    }}
                >
                    <IconButton
                        onClick={() => setEntryOpen(false)}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <IncomeEntry
                        onClose={() => setEntryOpen(false)}
                        onSaved={() => {
                            setEntryOpen(false);
                            loadIncomes();
                        }}
                    />
                </Box>
            </Modal>
        </Box>
    );
}
